import TopicHeader from '../../../Utilities/TopicHeader';
import ReferralSearchHead from './ReferralSearchHead';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { format, subDays } from 'date-fns';
import { useSelector } from 'react-redux';
import { post } from '../../Auths/AuthToken';
import setUiMessage from '../../../Utilities/Tostify';
import Pagination from '../../../Utilities/Pagination';
import ReferralList from './ReferralList';
import { debounce } from 'lodash';
import AddReferralPopup from './AddReferralPopup';
import ConfirmDialog from '../../../Utilities/ConfirmDialog';

function Referral() {
  const { user } = useSelector((store) => store.auth);
  const [openNewReferralForm, setOpenNewReferralForm] = useState({
    status: false,
    refId: null,
    rptSentId: null,
    patientId: null,
    reportId: null,
  });
  const [limit, setSetLimit] = useState(200);
  const [offset, setSetOfset] = useState(0);
  const [allData, setAllData] = useState([]);
  const [reload, setReload] = useState(false);

  const [referralData, setReferralData] = useState({
    startDate: subDays(new Date(), 90),
    endDate: new Date(),
    filterValue: '90D',
    isDateRange: false,
    provider: { value: 'ALL', label: 'ALL REFERRAL PROVIDER' },
    searchTermName: '',
    searchTermUrgency: 'ALL',
    searchTermStatus: '1',
  });

  const [deleteReferral, setDeleteReferral] = useState({
    open: false,
    refId: '',
  });

  const handleFilterChange = useCallback((data) => {
    setReferralData(data);
    setSetOfset(0);
  }, []);

  const handlePageChange = useCallback((data) => {
    setSetLimit(data[1]);
    setSetOfset(data[0]);
  }, []);

  const fetchResults = useCallback(async () => {
    try {
      const response = await post(
        'api/patient/referral/loadReferralGridView',
        {
          ...referralData,
          loginUserId: user?.id,
          limit,
          offset,
          startDate: referralData.startDate
            ? format(referralData.startDate, 'yyyy-MM-dd')
            : '',
          endDate: referralData.endDate
            ? format(referralData.endDate, 'yyyy-MM-dd')
            : '',
          provider: referralData.provider?.value || '',
        },
      );

      if (response.status === 200) {
        setAllData(response.data);
      }
    } catch (err) {
      setUiMessage('err', err?.message);
    }
  }, [referralData, limit, offset, user?.id]);

  const debouncedFetch = useMemo(
    () => debounce(fetchResults, 500),
    [fetchResults],
  );

  useEffect(() => {
    if (
      referralData.isDateRange &&
      (!referralData.startDate || !referralData.endDate)
    ) {
      return;
    }

    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
    };
  }, [referralData, limit, offset, user?.id, reload, debouncedFetch]);

  const reopenReferralPopup = (refId, rptSentId, patientId, reportId) => {
    setOpenNewReferralForm({
      status: true,
      refId,
      rptSentId,
      patientId,
      reportId,
    });
  };

  const confirmDeleteReferral = async () => {
    try {
      if (deleteReferral?.refId) {
        const response = await post('api/CommonTask/deleteReferralRow', {
          refId: deleteReferral.refId,
        });
        if (response.status === 200) {
          setUiMessage('suc', 'Referral deleted successfully.');
          setReload(!reload);
          setDeleteReferral({
            open: false,
            refId: '',
          });
        }
      }
    } catch (error) {
      setUiMessage('err', error.message);
    }
  };

  const handleChangeStatus = async (refId, status) => {
    try {
      const response = await post('api/patient/referral/updateReferralStatus', {
        refId,
        status,
      });
      if (response.status === 200) {
        setUiMessage('suc', 'Status updated successfully.');
        setReload(!reload);
      }
    } catch (error) {
      setUiMessage('err', error.message);
    }
  };

  const openNewReferral = () => {
    setOpenNewReferralForm({
      status: true,
      refId: null,
      rptSentId: null,
      patientId: null,
      reportId: null,
    });
  };

  return (
    <div id="referralMaingrid" className="preauth-Maingrid-Pat">
      <TopicHeader title="Referral:" />
      <ReferralSearchHead
        referralData={referralData}
        providerOptions={allData?.allProviders || []}
        setData={handleFilterChange}
        openPopup={openNewReferral}
      />
      <div className="intake-paging">
        <Pagination
          totalRecord={Number(allData?.rowCount) || 0}
          onPageChange={handlePageChange}
          offset={offset}
          rowPerPage={limit}
        />
      </div>

      <div className="w-100 fs-13 overflow-x-auto">
        <div className="referral-grid header gridRow items-start">
          <div className="border-r border-r-lightgray p-2 bg-gray-100 text-dark fw-600">
            Status
          </div>
          <div className="border-r border-r-lightgray p-2 bg-gray-100 text-dark fw-600">
            Patient Name
          </div>
          <div className="border-r border-r-lightgray p-2 bg-gray-100 text-dark fw-600">
            Referring Provider
          </div>
          <div className="border-r border-r-lightgray p-2 bg-gray-100 text-dark fw-600">
            Referral Date
          </div>
          <div className="border-r border-r-lightgray p-2 bg-gray-100 text-dark fw-600">
            Referred To
          </div>
          <div className="border-r border-r-lightgray p-2 bg-gray-100 text-dark fw-600">
            Urgency
          </div>
          <div className="border-r border-r-lightgray p-2 bg-gray-100 text-dark fw-600">
            Reason
          </div>
          <div className="border-r border-r-lightgray p-2 bg-gray-100 text-dark fw-600">
            Appt. Scheduled / Patient Informed
          </div>
          <div className="border-r border-r-lightgray p-2 bg-gray-100 text-dark fw-600">
            Appt. Date
          </div>
          <div className="border-r border-r-lightgray p-2 bg-gray-100 text-dark fw-600">
            Sent Via
          </div>
        </div>

        {allData?.referralAllData?.map((item, i) => (
          <div
            key={item.id || i}
            className={`referral-grid row gridRow ${i % 2 === 0 ? 'even' : 'odd'}`}
            onClick={() =>
              reopenReferralPopup(
                item.id,
                item.rptSentId,
                item.patient_id,
                item.report_id,
              )
            }
          >
            <ReferralList item={item} handleChangeStatus={handleChangeStatus} />
          </div>
        ))}
      </div>

      {openNewReferralForm.status && (
        <AddReferralPopup
          isOpen={openNewReferralForm.status}
          onClose={() =>
            setOpenNewReferralForm({
              status: false,
              refId: null,
              rptSentId: null,
              patientId: null,
              reportId: null,
            })
          }
          handleReload={() => setReload(!reload)}
          refId={openNewReferralForm.refId}
          rptSentId={openNewReferralForm.rptSentId}
          patientId={openNewReferralForm.patientId}
          reportId={openNewReferralForm.reportId}
        />
      )}
      {deleteReferral.open && (
        <ConfirmDialog
          title={`Confirm!`}
          bodytext={`Do you really want to delete this referral data?`}
          closeDialog={() =>
            setDeleteReferral({
              open: false,
              refId: '',
            })
          }
          successDialog={confirmDeleteReferral}
        />
      )}
    </div>
  );
}

export default Referral;
